<template>
  <div class="vacancy-form">
    <object-vacancy-form-status
      :is-date-selected="storeStartDate !== null"
      :price="priceData.status === 'occupied' ? false : priceData.priceObject.price"
      :error="!bookable"
    />

    <flat-pickr-base
      v-if="hasObjectVacancy"
      :key="flatpickrBaseUpdateKey"
      ref="flatpickr"
      v-model="opmMixin_fpModelValue"
      :disabled="isFetchingPrice"
      :vacancy-data="pickerVacancyData"
      :viewed-date="opmMixin_objectViewedPickerDate"
      :extended-input-emit="true"
      :month-count="2"
      :force-right-most="true"
      :mode="calendarMode"
      :static="true"
      @on-view-change="$store.commit('SET_OBJECT_PICKER_VIEWED_DATE', $event)"
    />

    <div
      v-if="hasObjectVacancy"
      class="form-group"
      :class="{ disabled: isFetchingPrice }"
    >
      <select-persons
        v-model="opmMixin_selectedPersons"
        :value="opmMixin_selectedPersons"
        :persons-max="objectData.personsMax"
        :adults-max="objectData.adultsMax"
      />
    </div>
    <!-- {{ objectData }} -->
    <object-vacancy-price
      v-if="hasObjectVacancy"
      :has-vacancy-data="hasObjectVacancy"
      :data="priceData"
      :params="queryParams"
      :price-min="objectData.minPrice"
    />

    <div
      v-if="availableSections.ratings"
      class="rating-stars"
    >
      <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
      <a
        v-smooth-scroll="{
          duration: 200,
          updateHistory: false,
        }"
        target="_self"
        href="#bewertungen"
        class="nav-link"
      >
        <object-ratings-aggregation
          v-if="hasObjectDataByKey('ratingCount')"
          :total="aggregationCount"
          :average="aggregationAverage"
          :show-average-text-below="true"
          :average-of-total="{written:false,hidden:false}"
        />
      </a>
    </div>
    <!-- eslint-disable max-len -->
    <!-- eslint-enable max-len -->
  </div>
</template>

<script>
// import axios from 'apiclient';
import FlatPickrBase from 'components/FlatPickrBase.vue';
import ObjectVacancyPrice from 'components/ObjectVacancyPrice.vue';
import ObjectVacancyFormStatus from 'components/ObjectVacancyFormStatus.vue';
import SelectPersons from 'components/SelectPersons.vue';
import ObjectRatingsAggregation from 'components/ObjectRatingsAggregation.vue';

import ActiveObjectStore from 'mixins/ActiveObjectStore';
import ObjectPriceModels from 'mixins/ObjectPriceModels';
import SocialShareLinkBuilder from 'mixins/SocialShareLinkBuilder';
import { mapGetters } from 'vuex';

/**
 * @@author        j0Shi
 * @@date          2019.06.18
 * @@desc          Form on object page to display picker and sticky price widget
 * @@usedby        Object
 */
export default {
  name: 'ObjectVacancyForm',
  components: {
    FlatPickrBase,
    SelectPersons,
    ObjectVacancyPrice,
    ObjectVacancyFormStatus,
    ObjectRatingsAggregation,
  },
  mixins: [
    // store helper with necessary getters to deal with active objects
    ActiveObjectStore,
    // v-models for picker and persons input
    ObjectPriceModels,
    // social share links
    SocialShareLinkBuilder,
  ],
  props: {
    // disables sections / navitems in case some data is missing
    availableSections: {
      type: Object,
      required: true,
    },
    // vacancy data for the picker
    pickerVacancyData: {
      type: Object,
      default: () => ({
        count: 0,
        days: {},
        maxDate: new Date(),
      }),
    },
  },
  data() {
    return {
      // @vuese
      // flatpickr compliant calendar mode
      // "single", "multiple", or "range"
      // https://flatpickr.js.org/options/
      calendarMode: 'range',
      flatpickrBaseUpdateKey: 0,
    };
  },
  computed: {
    ...mapGetters([
      'getPriceByKey',
      'hasObjectDataByKey',
    ]),
    // @vuese
    // current price request data from the object store
    priceRequestData() {
      return this.$store.state.objects.priceRequestData;
    },
    // @vuese
    // ratingAverage from the object store
    aggregationAverage() {
      return this.getObjectDataByKey('ratingAverage');
    },
    // @vuese
    // ratingCount from the object store
    aggregationCount() {
      return this.getObjectDataByKey('ratingCount');
    },
    // @vuese
    // price data from the object store based on the identifierKey
    priceData() {
      return this.getPriceByKey(this.priceRequestData.identifierKey);
    },
    // @vuese
    // startDate from the object store, used to intialize the flatpickr
    storeStartDate() {
      return this.priceRequestData.startDate;
    },
    // @vuese
    // endDate from the object store, used to initialze the flatpickr
    storeEndDate() {
      return this.priceRequestData.endDate;
    },
    // @vuese
    // persons from the object store, used to initialize the persons select
    storePersons() {
      return this.priceRequestData.persons;
    },
    // @vuese
    // query param object that holds the params for api queries
    queryParams() {
      return {
        startDate: this.$route.query?.startDate || this.storeStartDate || undefined,
        endDate: this.$route.query?.endDate || this.storeEndDate || undefined,
        persons: this.$route.query?.persons || this.storePersons || 2,
        pm: this.$route.query?.pm || undefined,
      };
    },
    isFetchingPrice() {
      return this.$store.getters.getIsFetchingPrice;
    },
    clientname() {
      if (this.$store.getters.getPartnerConfigValue('partnerName') !== '') return this.$store.getters.getPartnerConfigValue('partnerName').replace('&', 'und');
      return process.env.CLIENTNAME;
    },
  },
  created() {
    // listen to store action for opening the pickr from other component
    this.$store.subscribeAction((action) => {
      if (action.type === 'OPEN_WIDGET_PICKR' && action.payload === true) {
        // update/re-render flatpickrBase component via :key update to get ref
        this.flatpickrBaseUpdateKey += 1;
        // await re-rendering with nextTick and timeout before opening flatpickr
        this.$nextTick(() => {
          if (this.$refs?.flatpickr?.$refs?.flatpickr?.fp) {
            this.openDatePicker();
          } else {
            this.$refs?.flatpickr?.initPicker();
          }
        });
      }
    });
  },
  methods: {
    // @vuese
    // open the date picker
    openDatePicker() {
      this.$refs.flatpickr.$refs.flatpickr.fp.open();
    },
  },
  i18nOptions: { namespaces: ['common', 'errors'] },
};
</script>

<style lang="scss" scoped>
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  /deep/ .vacancy-details {
    .vacancy-dates {
      margin-top: 0.1em;
    }

    margin: 0 0 0.5em;

    .price-reduction {
      justify-content: center;

      del {
        order: 2;
        margin-left: 0.5em;
      }

      .badge-price-reduction {
        margin-left: 0;
      }
    }
  }

  /deep/ .rating-stars {
    margin: 0.5em 0;
    justify-content: center;

    .description {
      text-align: center;
      display: block;
    }
  }

  /deep/ .btn-primary {
    margin: 0.75em 0 0.25em;
  }
</style>
